import { useEffect, useRef } from "react";

/**
 * How long a Teams call may keep ringing before the kiosk gives up on it.
 */
const RING_TIMEOUT_MS = 30000;

/**
 * Ends a call that never leaves the ringing state.
 *
 * When the host's tenant blocks external communication with our domain, the
 * invitation never reaches them and no error comes back either — the call
 * simply rings for as long as the page stays open, which on a kiosk means
 * until the next visitor walks up to it. Teams itself reports a host who is
 * only signed out within a few seconds, so this fires when nothing at all
 * answers.
 */
function useRingTimeout(
  isRinging: boolean,
  onTimeout: () => void,
  ms: number = RING_TIMEOUT_MS,
) {
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    if (!isRinging) {
      return undefined;
    }

    const timeout = setTimeout(() => onTimeoutRef.current(), ms);

    return () => clearTimeout(timeout);
  }, [isRinging, ms]);
}

export { useRingTimeout, RING_TIMEOUT_MS };
